import {
    createSingleValueFilterState,
    type SingleValueFilterState,
} from '@modules-rocco/filters/filter-state/single-value-filter-state';
import type { AggregationMultipleValuesResults } from '@modules-rocco/search/types/aggregations';

export interface MultipleValuesFilterState<T = string> {
    items: SingleValueFilterState<T>[];
    selectedItems: SingleValueFilterState<T>[];
    allowMultipleSelectedItems: boolean;
}

export const createMultipleValuesFilterState = <T = string>(
    state: PartialProps<
        MultipleValuesFilterState<T>,
        'allowMultipleSelectedItems'
    >,
): MultipleValuesFilterState<T> => ({
    ...state,
    items: state.items,
    selectedItems: state.selectedItems,
    allowMultipleSelectedItems:
        state.allowMultipleSelectedItems !== undefined
            ? state.allowMultipleSelectedItems
            : true,
});

export const createMultipleValuesFilterStateFromAggregationResult = <
    T = string,
>(
    aggregationResult?: AggregationMultipleValuesResults<T>,
    allowMultipleSelectedItems: boolean = true,
): MultipleValuesFilterState<T> => {
    // Create a Set from selectedItems for efficient lookup.
    const selectedItemValues = new Set(
        aggregationResult?.selectedItems?.map((item) => item.value) ?? [],
    );

    return createMultipleValuesFilterState<T>({
        items:
            aggregationResult?.items?.map((item) =>
                createSingleValueFilterState({
                    ...item,
                    checked: selectedItemValues.has(item.value),
                }),
            ) ?? [],
        selectedItems:
            aggregationResult?.selectedItems?.map((item) =>
                createSingleValueFilterState({
                    ...item,
                    checked: true,
                }),
            ) ?? [],
        allowMultipleSelectedItems,
    });
};

/**
 * Toggles the checked state of a specific item in a MultipleValuesFilterState.
 * If the item exists in the state, it updates its checked status and adjusts the selectedItems accordingly.
 * If the item doesn't exist, it returns the original state unchanged.
 *
 * @param state - The current MultipleValuesFilterState
 * @param item - The SingleValueFilterState item to toggle
 * @param checked - The new checked state for the item
 * @returns A new MultipleValuesFilterState with the updated item and selectedItems
 */
export const toggleMultipleListFilterStateItem = (
    state: MultipleValuesFilterState,
    item: SingleValueFilterState,
    checked: boolean,
): MultipleValuesFilterState => {
    const index = state.items.findIndex((i) => i.value === item.value);

    if (index > -1) {
        // Update the checked state of the target item
        state.items[index] = { ...item, checked };

        // If multiple items not allowed, uncheck all other items
        if (!state.allowMultipleSelectedItems) {
            state.items.forEach((i, idx) => {
                if (idx !== index) {
                    state.items[idx] = { ...i, checked: false };
                }
            });
        }
    }

    return createMultipleValuesFilterState({
        ...state,
        selectedItems:
            checked && index > -1
                ? !state.allowMultipleSelectedItems
                    ? [{ ...item, checked: true }] // Only keep the newly selected item
                    : [...state.selectedItems, { ...item, checked: true }]
                : state.selectedItems.filter((i) => i.value !== item.value),
    });
};
